import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../../../../translation/namespace';
import { CategoriesComboBox, Category } from './CategoriesComboBox';
import { sendLiveRegionMessage } from '../../../../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { CategoryListItem } from './CategoryListItem';
import { useHtmlValidId } from '../../../../../../hooks/useHtmlValidId';
import usePrevious from '../../../../../../hooks/usePrevious';

type CategoriesComboboxMultiSelectProps = {
  name: string;
  options: Category[];
  value: string[];
};

/**
 * CategoriesMultiSelect .
 * -> different from ComboboxMultiSelects as it's not connected to solr/backend via suggestUrl
 *    -> all available options are known from the start.
 *    -> filtering is done in frontend.
 * */
export function CategoriesComboboxMultiSelect({ name, options, value }: CategoriesComboboxMultiSelectProps) {
  const { t } = useTranslation(Namespace.NavigationPropertiesForm); // where to put translations ?
  const id = useHtmlValidId();
  const comboboxCategoriesId = `categories-combobox-multi-select-${id}`;
  const [selectedCategories, setSelectedCategories] = useState<Category[]>(() => {
    return options.filter(option => value.includes(option.value));
  });
  const previousSelectedOptions = usePrevious(selectedCategories);

  // focusing the input when removing items
  useEffect(() => {
    if (previousSelectedOptions && previousSelectedOptions.length > selectedCategories.length) {
      const element = document.getElementById(comboboxCategoriesId);
      if (element) {
        element.focus();
      }
    }
  }, [previousSelectedOptions, selectedCategories.length, id, comboboxCategoriesId]);

  const removeItem = (item: Category) => {
    sendLiveRegionMessage(t('inputs.categories.liveRegion.itemRemoved', { item: item.text }));
    setSelectedCategories(prevState => prevState.filter(category => category.value !== item.value));
  };

  const addItem = (item: Category) => {
    if (!selectedCategories.some(cat => cat.value === item.value)) {
      sendLiveRegionMessage(t('inputs.categories.liveRegion.itemAdded', { item: item.text }));
      setSelectedCategories(prevState => [...prevState, item]);
    }
  };
  return (
    <div className="categories-combobox-multi-select">
      <CategoriesComboBox
        id={comboboxCategoriesId}
        labelTitle={t('inputs.categories.label')}
        options={options}
        onSelection={(item) => {
          if (item) {
            addItem(item);
          }
        }}
      />
      {selectedCategories.length > 0 && (
        <>
          <h4 className="invisible">{t('inputs.categories.selected')}</h4>
          <ul className="categories-combobox-multi-select__resultlist">
            {selectedCategories.map(item => (
              <li key={item.value}>
                <input type="hidden" value={item.value} name={name} />
                <CategoryListItem
                  title={item.text}
                  hexColor={item.hexColor}
                  remove={() => removeItem(item)}
                  removeText={t('inputs.categories.remove', { item: item.text }) || undefined}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
